import React, { useCallback } from 'react';
import { getSnapshot } from 'mobx-state-tree';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import colors from 'tailwindcss/colors';
import Box from 'ui-box';
import {
  Button,
  Card,
  Container,
  FormField,
  Grid,
  Heading,
  HTMLSelect,
  InputGroup,
  majorScale,
  PageContainer,
  TextArea,
  useToast,
} from '../../../npaper';
import { PDFRenderer } from '../../../npaper/PDFRenderer';
import { IOrganisation, useStores } from '../../../stores';
import querystring from 'querystring';
import { useState } from 'react';
import { useDebounce } from 'use-debounce/lib';
import { useEffect } from 'react';

type FormValues = {
  logo_size?: string | null;
  logo_placement?: string | null;
  brand_color?: string | null;

  offer_note?: string | null;
  order_note?: string | null;
  invoice_note?: string | null;
};

const TemplatesPage = () => {
  const history = useHistory();
  const intl = useIntl();
  const toaster = useToast();
  const { authStore, organisationStore } = useStores();
  const organisation = authStore.currentOrganisation!;

  const { register, errors, handleSubmit, watch, control, formState } =
    useForm<FormValues>({
      mode: 'all',
      defaultValues: {
        brand_color: organisation.brand_color,
        logo_placement: organisation.logo_placement,
        logo_size: organisation.logo_size,
        offer_note: organisation.offer_note,
        order_note: organisation.order_note,
        invoice_note: organisation.invoice_note,
      },
    });

  const previewQuery = querystring.stringify(
    watch(['logo_size', 'logo_placement', 'brand_color'])
  );

  const computePdfUrl = useCallback(
    () =>
      `${organisationStore.apiUrl}${organisationStore.apiPath}/${
        organisation.id
      }/preview-template/?${previewQuery ? previewQuery : ''}`,
    [
      organisation.id,
      organisationStore.apiPath,
      organisationStore.apiUrl,
      previewQuery,
    ]
  );

  const [_pdfUrl, setPdfUrl] = useState(computePdfUrl);

  const [pdfUrl] = useDebounce(_pdfUrl, 200);

  useEffect(() => {
    console.log(errors, previewQuery);
    if (Object.keys(errors || {}).length > 0) return;
    setPdfUrl(computePdfUrl());
  }, [computePdfUrl, errors, previewQuery, setPdfUrl]);

  const onSubmit = async (values: FormValues) => {
    try {
      await organisationStore.updateItem(organisation, {
        ...getSnapshot(organisation as IOrganisation),
        ...(values as any),
        next_company_number: undefined,
        next_order_number: undefined,
        next_offer_number: undefined,
        next_invoice_number: undefined,
      });
      toaster.addToast({
        message: intl.formatMessage({
          defaultMessage: 'Template has been saved',
        }),
        intent: 'success',
      });
    } catch (err) {
      toaster.showDefaultError(err);
    }
  };

  const noteMessage = intl.formatMessage({
    defaultMessage: 'Note',
  });
  const maxLengthMessage = intl.formatMessage({
    defaultMessage: 'Note should be 500 characters or less',
  });

  return (
    <PageContainer>
      <Container>
        <Button
          iconLeft="arrow-left"
          marginBottom={majorScale(2)}
          onClick={() =>
            history.length > 0 ? history.goBack() : history.push('/settings')
          }
        >
          <FormattedMessage defaultMessage="Settings" />
        </Button>

        <Grid>
          <Card className="col-span-12 md:col-span-12 lg:col-span-10 xl:col-span-8">
            <div className="dialog">
              <Box className="header">
                <Heading size={400}>
                  <FormattedMessage defaultMessage="Base template" />
                </Heading>
              </Box>
              <Grid padding={majorScale(2)} className="border-bottom">
                <Heading size={300} className="col-span-12">
                  <FormattedMessage defaultMessage="Logo" />
                </Heading>

                <FormField
                  label={intl.formatMessage({ defaultMessage: 'Size' })}
                  inline
                  className="col-span-9"
                >
                  <Controller
                    control={control}
                    name="logo_size"
                    defaultValue="small"
                    render={({ onChange, value }) => (
                      <HTMLSelect
                        onChange={onChange}
                        value={value}
                        options={[
                          {
                            value: 'small',
                            label: intl.formatMessage({
                              defaultMessage: 'Small',
                            }),
                          },
                          {
                            value: 'medium',
                            label: intl.formatMessage({
                              defaultMessage: 'Medium',
                            }),
                          },
                          {
                            value: 'large',
                            label: intl.formatMessage({
                              defaultMessage: 'Large',
                            }),
                          },
                        ]}
                      />
                    )}
                  />
                </FormField>

                <FormField
                  label={intl.formatMessage({ defaultMessage: 'Placement' })}
                  inline
                  className="col-span-9"
                >
                  <Controller
                    control={control}
                    name="logo_placement"
                    defaultValue="right"
                    render={({ onChange, value }) => (
                      <HTMLSelect
                        onChange={onChange}
                        value={value}
                        options={[
                          {
                            value: 'left',
                            label: intl.formatMessage({
                              defaultMessage: 'Left',
                            }),
                          },
                          {
                            value: 'right',
                            label: intl.formatMessage({
                              defaultMessage: 'Right',
                            }),
                          },
                        ]}
                      />
                    )}
                  />
                </FormField>

                <FormField
                  label={intl.formatMessage({ defaultMessage: 'Brand Color' })}
                  inline
                  className="col-span-9"
                >
                  <InputGroup
                    name="brand_color"
                    placeholder={intl.formatMessage({
                      defaultMessage: 'Enter your brand color as HEX value',
                    })}
                    ref={register({
                      validate: (val: string) =>
                        !val ||
                        /^#+([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$/.test(val) ||
                        intl.formatMessage({
                          defaultMessage: 'Not a valid HEX color code',
                        }),
                    })}
                    iconRight="full-circle"
                    iconColor={watch('brand_color') || colors.cyan[500]}
                    isInvalid={!!errors.brand_color}
                    validationMessage={
                      errors.brand_color
                        ? errors.brand_color.message
                        : intl.formatMessage({
                            defaultMessage:
                              'Enter your brand color as HEX color code, e.g. #06B6D4',
                          })
                    }
                  />
                </FormField>
              </Grid>

              <Grid padding={majorScale(2)} className="border-bottom">
                <Heading size={300} className="col-span-12">
                  <FormattedMessage defaultMessage="Offers" />
                </Heading>

                <FormField label={noteMessage} inline className="col-span-9">
                  <TextArea
                    name="offer_note"
                    ref={register({
                      maxLength: {
                        value: 500,
                        message: maxLengthMessage,
                      },
                    })}
                    placeholder={intl.formatMessage({
                      defaultMessage:
                        'Enter an optional note at the end of every offer',
                    })}
                    maxLength={500}
                    validationMessage={
                      errors.offer_note ? errors.offer_note.message : undefined
                    }
                    isInvalid={!!errors.offer_note}
                  />
                </FormField>
              </Grid>

              <Grid padding={majorScale(2)} className="border-bottom">
                <Heading size={300} className="col-span-12">
                  <FormattedMessage defaultMessage="Orders" />
                </Heading>

                <FormField label={noteMessage} inline className="col-span-9">
                  <TextArea
                    name="order_note"
                    ref={register({
                      maxLength: {
                        value: 500,
                        message: maxLengthMessage,
                      },
                    })}
                    placeholder={intl.formatMessage({
                      defaultMessage:
                        'Enter an optional note at the end of every order',
                    })}
                    maxLength={500}
                    validationMessage={
                      errors.order_note ? errors.order_note.message : undefined
                    }
                    isInvalid={!!errors.order_note}
                  />
                </FormField>
              </Grid>

              <Grid padding={majorScale(2)} className="border-bottom">
                <Heading size={300} className="col-span-12">
                  <FormattedMessage defaultMessage="Invoices" />
                </Heading>

                <FormField label={noteMessage} inline className="col-span-9">
                  <TextArea
                    name="invoice_note"
                    ref={register({
                      maxLength: {
                        value: 500,
                        message: maxLengthMessage,
                      },
                    })}
                    placeholder={intl.formatMessage({
                      defaultMessage:
                        'Enter an optional note at the end of every invoice',
                    })}
                    maxLength={500}
                    validationMessage={
                      errors.invoice_note
                        ? errors.invoice_note.message
                        : undefined
                    }
                    isInvalid={!!errors.invoice_note}
                  />
                </FormField>
              </Grid>

              <Grid padding={majorScale(2)}>
                <Heading size={300} className="col-span-12">
                  <FormattedMessage defaultMessage="Preview" />
                </Heading>

                <div className="col-span-12 lg:col-span-6 lg:col-start-4 flex justify-center">
                  <PDFRenderer url={pdfUrl} language={''} />
                </div>
              </Grid>

              <Box className="actions">
                <Button
                  disabled={formState.isSubmitting}
                  onClick={() => history.push('/settings')}
                >
                  <FormattedMessage
                    id="defaults.cancel"
                    defaultMessage="Cancel"
                  />
                </Button>
                <Button
                  primary
                  isLoading={formState.isSubmitting}
                  onClick={handleSubmit(onSubmit)}
                >
                  <FormattedMessage
                    id="defaults.save-changes"
                    defaultMessage="Save changes"
                  />
                </Button>
              </Box>
            </div>
          </Card>
        </Grid>
      </Container>
    </PageContainer>
  );
};

export default TemplatesPage;
